import { useMutation } from '@tanstack/react-query'
import {
  AgxColours,
  AgxFileUpload,
  EntityImage,
} from '@urbanx/agx-ui-components'
import {
  UploadedDocument,
  useInvalidateUseGetAgencyMembers,
  useRemovePublicDocument,
} from '@urbanx/agx.agencies.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import { AgxButton, AgxRow } from '@urbanx/agx.ui-components2'
import { useCallback, useContext, useEffect, useState } from 'react'
import { UploadPublicDocument } from 'services'

interface LogoUploadProps {
  logo?: UploadedDocument
  onImageChange: (doc?: UploadedDocument) => void
  agencyId: string
  isDisabled?: boolean
}

const LogoUpload = ({
  logo,
  onImageChange,
  agencyId,
  isDisabled = false,
}: LogoUploadProps) => {
  const [imagePublicPath, setImagePublicPath] = useState(logo?.publicPath)

  useEffect(() => {
    setImagePublicPath(logo?.publicPath)
  }, [logo])

  const { getAuthToken } = useContext(AuthContext)

  const { mutate: uploadPublicDocument, isPending: isUploadingLogo } =
    useMutation({
      mutationFn: UploadPublicDocument,
      onSuccess: (r) => {
        setImagePublicPath(r?.publicPath || '')
        onImageChange(r)
      },
    })

  const invalidateAgencyMembers = useInvalidateUseGetAgencyMembers()

  const {
    removePublicDocument: deleteLogo,
    removePublicDocumentPending: isRemovingLogo,
  } = useRemovePublicDocument({
    onSuccess: async () => {
      onImageChange(undefined)
      await invalidateAgencyMembers(agencyId)
      setImagePublicPath(undefined)
    },
    onError: () => {},
  })

  const isPhotoActionPending = isUploadingLogo || isRemovingLogo

  const uploadLogo = useCallback(
    (file: File) => {
      if (isDisabled || isPhotoActionPending) return Promise.resolve(undefined)

      setImagePublicPath(URL.createObjectURL(file))
      return new Promise((resolve, reject) => {
        uploadPublicDocument(
          {
            selectedFile: file,
            AgencyId: agencyId,
            getAuthToken,
          },
          {
            onSuccess: resolve,
            onError: reject,
          }
        )
      })
    },
    [
      setImagePublicPath,
      uploadPublicDocument,
      agencyId,
      getAuthToken,
      isDisabled,
      isPhotoActionPending,
    ]
  )

  return (
    <AgxRow gap="spacing-2xl" alignItems="alignCenter" fill>
      <EntityImage
        addRingToImage
        backgroundColour={`var(--${AgxColours.NEUTRAL_GREY_500})`}
        photoUri={imagePublicPath}
        size={72}
        usePlaceholderImage
      />
      <AgxRow gap="spacing-2xl" alignItems="alignCenter" justifyContent="center">
        <AgxFileUpload
          id="fileUpload"
          title=""
          buttonText={imagePublicPath ? 'Change' : 'Upload'}
          isImageFileType
          disabled={isDisabled || isPhotoActionPending}
          uploadFile={(file) => uploadLogo(file)}
          extensions={['jpg', 'jpeg', 'png', 'svg', 'bmp']}
        />
        {imagePublicPath && (
          <AgxButton
            id="removeLogoButton"
            text="Remove"
            size="medium"
            variant="hollow"
            disabled={isDisabled || isPhotoActionPending}
            onClick={() => {
              if (isDisabled || isPhotoActionPending) return
              deleteLogo({
                filePath: logo?.containerFilePath || '',
              })
            }}
          />
        )}
      </AgxRow>
    </AgxRow>
  )
}

export default LogoUpload
